import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, Clock, ChevronRight, Award } from 'lucide-react';
import { assessmentApi } from '../services/api';
import toast from 'react-hot-toast';

/**
 * Student Assessments — standalone list of every quiz they can take.
 * Trainer/admin also lands here; for them Manage LMS is the primary tool.
 */
export default function Assessments() {
  const [assessments, setAssessments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    assessmentApi.getAssessments()
      .then(res => setAssessments(res.data))
      .catch(() => toast.error('Failed to load assessments'))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Assessments</h1>
      <p className="text-gray-600 dark:text-gray-400 mb-6">Take quizzes to test what you've learned. Auto-graded, instant results.</p>

      {assessments.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <ClipboardCheck className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">No assessments available yet.</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Check back later — new quizzes are added regularly.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {assessments.map(a => (
            <div
              key={a.id}
              onClick={() => navigate(`/lms/assessments/${a.id}`)}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 cursor-pointer hover:shadow-md hover:border-primary-400 dark:hover:border-primary-500 transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{a.title}</h3>
                  {a.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{a.description}</p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-gray-500 dark:text-gray-400">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 font-medium capitalize">
                  {a.assessment_type || 'quiz'}
                </span>
                <span className="inline-flex items-center">
                  <Award className="w-3.5 h-3.5 mr-1" />
                  {a.question_count || 0} question{a.question_count === 1 ? '' : 's'}
                </span>
                {a.time_limit_minutes && (
                  <span className="inline-flex items-center">
                    <Clock className="w-3.5 h-3.5 mr-1" />
                    {a.time_limit_minutes} min
                  </span>
                )}
                {a.levels && a.levels.map(l => (
                  <span key={l} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${l === 'L1' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'}`}>{l}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
